import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Badge,
  Spinner,
} from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import SimpleHeader from "components/Headers/SimpleHeader";
import Pagination from "../components/Pagination";
import userServices from "services/userServices";
import helpers, { LOGS_PAGE_SIZE_OPTIONS } from "utils/helpers";
import { useTranslation } from "context/TranslationProvider";
import { useNotification } from "providers/NotificationProvider";
import { useAuth } from "context/AuthContext";
import IpStatusReasonModal from "./IpStatusReasonModal";
import ManualBlockModal from "./ManualBlockModal";

const getSeverityClass = (sev) => {
  switch (String(sev).toUpperCase()) {
    case "CRITICAL":
      return "badge-severity-critical";
    case "HIGH":
      return "badge-severity-high";
    case "MEDIUM":
      return "badge-severity-medium";
    default:
      return "badge-severity-low";
  }
};

const REASON_LABELS = {
  BRUTE_FORCE_DETECTED: "Brute Force Attempt",
  PASSWORD_SPRAYING_DETECTED: "Password Spraying Attack",
  RATE_LIMIT_TRIGGERED: "Rate Limit Exceeded",
  EXCESSIVE_LOGIN_FAILURES: "Excessive Login Failures",
  HIGH_VELOCITY_LOGIN_ACTIVITY: "High Velocity Login Activity",
  HIGH_VELOCITY_ATTACK: "High Velocity Attack",
  REPEATED_SECURITY_VIOLATIONS: "Repeated Security Violations",
  SUSPICIOUS_LOGIN: "Suspicious Login Activity",
  MANUAL_IP_BLOCKED: "Manual Admin Block",
  MANUAL_IP_UNBLOCKED: "Manual Admin Unblock",
};

const REASON_TRANSLATION_MAP = {
  "Suspicious Activity": "page.blockedIps.reasons.suspiciousActivity",
  "Abuse / Malicious Traffic": "page.blockedIps.reasons.maliciousTraffic",
  "Brute Force Attempt": "page.blockedIps.reasons.bruteForce",
  "Bot / Automated Traffic": "page.blockedIps.reasons.botTraffic",
  "Security Investigation": "page.blockedIps.reasons.securityInvestigation",
  "Other": "page.blockedIps.reasons.other",
  "Manual action": "page.blockedIps.reasons.manualAction",
  "Security risk detected": "page.blockedIps.reasons.securityRisk",
  PASSWORD_SPRAYING_DETECTED: "page.blockedIps.reasons.passwordSpraying",
  RATE_LIMIT_TRIGGERED: "page.blockedIps.reasons.rateLimit",
  EXCESSIVE_LOGIN_FAILURES: "page.blockedIps.reasons.excessiveFailures",
  HIGH_VELOCITY_LOGIN_ACTIVITY: "page.blockedIps.reasons.highVelocity",
  HIGH_VELOCITY_ATTACK: "page.blockedIps.reasons.highVelocity",
  REPEATED_SECURITY_VIOLATIONS: "page.blockedIps.reasons.repeatedViolations",
  SUSPICIOUS_LOGIN: "page.blockedIps.reasons.suspiciousLogin",
  MANUAL_IP_BLOCKED: "page.blockedIps.reasons.manualBlocked",
  MANUAL_IP_UNBLOCKED: "page.blockedIps.reasons.manualUnblocked",
  "Multiple failed login attempts": "page.blockedIps.reasons.multipleFailedAttempts",
  "Admin permanently unblocked": "page.blockedIps.reasons.adminPermanentlyUnblocked",
  "Admin unblocked IP": "page.blockedIps.reasons.adminUnblockedIp",
  "Admin manual block": "page.blockedIps.reasons.adminManualBlock",
  "Auto-blocked by security policy": "page.blockedIps.reasons.autoBlockedPolicy",
};

const formatReason = (text, t) => {
  if (!text) return "N/A";
  if (typeof text !== "string") return String(text);
  if (REASON_TRANSLATION_MAP[text]) {
    return t ? t(REASON_TRANSLATION_MAP[text], REASON_LABELS[text] || text) : (REASON_LABELS[text] || text);
  }
  if (REASON_LABELS[text]) {
    return t ? t(`page.blockedIps.reasons.${text}`, REASON_LABELS[text]) : REASON_LABELS[text];
  }
  if (text.includes("_")) {
    const formatted = text
      .split("_")
      .map((w) => (w.length > 0 ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ""))
      .join(" ");
    return t ? t(`page.blockedIps.reasons.${text}`, formatted) : formatted;
  }
  return text;
};

const IpSecurityHistoryPage = () => {
  const { ipAddress: rawIp } = useParams();
  const ipAddress = decodeURIComponent(rawIp || "");
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { notify } = useNotification();
  const { permission } = useAuth();
  const blockedIpsPermission = permission?.permissions?.modules?.failedLogin;

  const tRef = useRef(t);
  tRef.current = t;
  const notifyRef = useRef(notify);
  notifyRef.current = notify;

  const [ipDetail, setIpDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(true);

  // History table state
  const [events, setEvents] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [tableState, setTableState] = useState({ page: 1, limit: 10, sortBy: "timestamp", sortOrder: "desc" });
  const [meta, setMeta] = useState({ totalItems: 0, totalPages: 0 });

  // Modals
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);

  // Format date with time
  const formatFullDateTime = useCallback((date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "N/A";
    const dateStr = helpers.getFormattedDate(date, t);
    const timeStr = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
    return `${dateStr}, ${timeStr}`;
  }, [t]);

  // Fetch IP details
  const fetchIpDetail = useCallback(async () => {
    if (!ipAddress) return;
    try {
      setLoadingDetail(true);
      const res = await userServices.getIpDetails(ipAddress);
      if (res?.success && res?.data) {
        setIpDetail(res.data);
      } else {
        notifyRef.current("error", tRef.current(res?.error || "page.blockedIps.fetchFailed", res?.message || "Failed to load IP details"));
      }
    } catch {
      notifyRef.current("error", tRef.current("page.users.toast.somethingWentWrong", "Something went wrong"));
    } finally {
      setLoadingDetail(false);
    }
  }, [ipAddress]);

  // Fetch History
  const fetchHistory = useCallback(async () => {
    if (!ipAddress) return;
    try {
      setLoadingHistory(true);
      const res = await userServices.getIpSecurityHistory(ipAddress, {
        page: tableState.page,
        limit: tableState.limit,
        sortOrder: tableState.sortOrder,
      });
      if (res?.success) {
        setEvents(res.data || []);
        setMeta(res.meta || { totalItems: 0, totalPages: 0 });
      } else {
        notifyRef.current("error", tRef.current(res?.error || "page.securityEvents.fetchFailed", "Failed to load history"));
      }
    } catch {
      notifyRef.current("error", tRef.current("page.users.toast.somethingWentWrong", "Something went wrong"));
    } finally {
      setLoadingHistory(false);
    }
  }, [ipAddress, tableState.page, tableState.limit, tableState.sortOrder]);

  useEffect(() => {
    fetchIpDetail();
  }, [fetchIpDetail]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleStatusConfirm = async (data) => {
    try {
      const res = await userServices.updateIPStatus({ ...data, isActive: false });
      if (res?.success) {
        notify("success", t("page.blockedIps.unblockSuccess", "IP unblocked successfully"));
        fetchIpDetail();
        fetchHistory();
        setStatusModalOpen(false);
      } else {
        notify("error", t(res?.error || "common.failed", res?.message || "Failed"));
      }
    } catch {
      notify("error", t("page.users.toast.somethingWentWrong", "Something went wrong"));
    }
  };

  const handleBlockConfirm = async (data) => {
    try {
      const res = await userServices.updateIPStatus({ ...data, isActive: true });
      if (res?.success) {
        notify("success", t("page.blockedIps.blockSuccess", "IP blocked successfully"));
        fetchIpDetail();
        fetchHistory();
        setBlockModalOpen(false);
      } else {
        notify("error", t(res?.error || "common.failed", res?.message || "Failed"));
      }
    } catch {
      notify("error", t("page.users.toast.somethingWentWrong", "Something went wrong"));
    }
  };

  // Duration & Remaining calculation
  const durationInfo = useMemo(() => {
    if (!ipDetail || !ipDetail.isActive) return null;
    if (ipDetail.blockType === "PERMANENT" || !ipDetail.blockedUntil) {
      return { isPermanent: true, label: t("page.blockedIps.status.permanent", "Permanent"), remainingText: t("page.blockedIps.neverExpires", "Never expires") };
    }
    const until = new Date(ipDetail.blockedUntil).getTime();
    const remainingMs = until - Date.now();
    if (remainingMs <= 0) {
      return { isExpired: true, label: t("page.blockedIps.status.expired", "Expired"), remainingText: t("page.blockedIps.status.expired", "Expired") };
    }

    const remainingMin = Math.max(1, Math.ceil(remainingMs / 60000));
    const remainingText = helpers.formatRemainingTime(remainingMs, t);

    let durationLabel = "";
    if (ipDetail.blockedAt) {
      const totalMs = until - new Date(ipDetail.blockedAt).getTime();
      const totalMin = Math.round(totalMs / 60000);
      if (totalMin >= 10 && totalMin <= 25) durationLabel = t("page.blockedIps.durations.15m", "15 Min");
      else if (totalMin >= 100 && totalMin <= 140) durationLabel = t("page.blockedIps.durations.2h", "2 Hours");
      else if (totalMin >= 1400 && totalMin <= 1500) durationLabel = t("page.blockedIps.durations.24h", "24 Hours");
      else if (totalMin >= 8000) durationLabel = t("page.blockedIps.durations.7d", "7 Days");
      else if (totalMin >= 60) durationLabel = `${Math.round(totalMin / 60)}h`;
      else durationLabel = `${totalMin}m`;
    } else {
      durationLabel = remainingMin <= 15
        ? t("page.blockedIps.durations.15m", "15 Min")
        : remainingMin <= 120
        ? t("page.blockedIps.durations.2h", "2 Hours")
        : remainingMin <= 1440
        ? t("page.blockedIps.durations.24h", "24 Hours")
        : t("page.blockedIps.durations.7d", "7 Days");
    }

    return { label: `${t("page.blockedIps.status.temporary", "Temporary")} (${durationLabel})`, remainingText };
  }, [ipDetail, t]);

  const canUpdate = blockedIpsPermission?.update;

  const handleTableChange = (type, { page: changePage, sizePerPage, sortField, sortOrder }) => {
    if (type === "sort" && sortField) {
      setTableState((prev) => ({
        ...prev,
        sortBy: sortField,
        sortOrder: sortOrder || "desc",
        page: 1,
      }));
    } else if (type === "pagination" && changePage) {
      setTableState((prev) => ({
        ...prev,
        page: changePage,
        limit: sizePerPage || prev.limit,
      }));
    }
  };

  const columns = useMemo(
    () => [
      {
        dataField: "timestamp",
        text: t("table.headers.dateTime", "DATE & TIME"),
        headerAlign: "left",
        align: "left",
        sort: true,
        formatter: (cell) => formatFullDateTime(cell),
      },
      {
        dataField: "eventType",
        text: t("table.headers.event", "EVENT"),
        headerAlign: "left",
        align: "left",
        formatter: (cell) => (
          <span className="text-sm font-weight-500 text-dark">
            {formatReason(cell, t)}
          </span>
        ),
      },
      {
        dataField: "severity",
        text: t("table.headers.severity", "SEVERITY"),
        headerAlign: "left",
        align: "left",
        formatter: (cell) => {
          const severityMap = {
            CRITICAL: t("page.blockedIps.severity.critical", "CRITICAL"),
            HIGH: t("page.blockedIps.severity.high", "HIGH"),
            MEDIUM: t("page.blockedIps.severity.medium", "MEDIUM"),
            LOW: t("page.blockedIps.severity.low", "LOW"),
          };
          const text = cell ? (severityMap[String(cell).toUpperCase()] || cell) : "N/A";
          return (
            <Badge
              className={`badge-pill px-2.5 py-0.5 text-uppercase text-xs ${getSeverityClass(cell)}`}
            >
              {text}
            </Badge>
          );
        },
      },
      {
        dataField: "source",
        text: t("table.headers.source", "SOURCE"),
        headerAlign: "left",
        align: "left",
        formatter: (cell, row) => {
          const isAdmin = cell === "ADMIN";
          return (
            <Badge
              className={`badge-pill px-2.5 py-0.5 text-uppercase ${isAdmin ? "badge-source-admin" : "badge-source-system"}`}
            >
              {cell === "ADMIN" ? t("page.blockedIps.sources.admin", "Admin") : t("page.blockedIps.sources.system", "System")}
              {row.performedBy && ` (${row.performedBy.firstName || row.performedBy.email})`}
            </Badge>
          );
        },
      },
      {
        dataField: "reason",
        text: t("table.headers.reason", "REASON"),
        headerAlign: "left",
        align: "left",
        formatter: (cell, row) => (
          <div className="text-left text-sm text-dark font-weight-500 text-wrap table-cell-max-300">
            {formatReason(cell || row.eventType, t)}
          </div>
        ),
      },
      {
        dataField: "details",
        text: t("table.headers.details", "DETAILS"),
        headerAlign: "left",
        align: "left",
        formatter: (_, row) => {
          const metaData = row.metadata || {};
          const badges = [];
          if (metaData.affectedAccountsCount != null) {
            badges.push(`${t("table.headers.affectedAccounts", "Accounts")}: ${metaData.affectedAccountsCount}`);
          }
          if (metaData.failureCount != null) {
            badges.push(`${t("table.headers.totalAttempts", "Failures")}: ${metaData.failureCount}`);
          }
          if (metaData.duration) {
            badges.push(`${t("table.headers.duration", "Duration")}: ${metaData.duration}`);
          }
          if (metaData.requestCount != null || metaData.rateLimitWindowCount != null) {
            badges.push(`${t("table.headers.requests", "Requests")}: ${metaData.requestCount ?? metaData.rateLimitWindowCount}`);
          }

          return (
            <div className="d-flex flex-column py-1 ip-details-badge-list">
              {badges.length > 0 ? (
                badges.map((b, i) => (
                  <span
                    key={i}
                    className="badge badge-light border text-dark font-weight-500 text-left ip-details-meta-badge"
                  >
                    {b}
                  </span>
                ))
              ) : (
                <span className="text-muted text-xs">N/A</span>
              )}
            </div>
          );
        },
      },
    ],
    [t, formatFullDateTime]
  );

  return (
    <div className="card-header-full-height">
      <SimpleHeader
        name={t("page.blockedIps.detailsTitle", "IP Details & Security History")}
        parentName={t("sideNav.failedLogin", "Failed Login")}
      />

      <Container className="mt--6" fluid>
        {/* ── CARD 1: IP OVERVIEW CARD (HEADER + 4 COLUMNS) ──────────────── */}
        <Card className="shadow-sm border-0 mb-4 ip-security-card">
          <div className="py-3 px-4 border-bottom d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
            <div>
              <h2 className="mb-0 text-dark font-weight-bold ip-heading-title">
                {t("page.blockedIps.detailsTitle", "IP Details & Security History")}
              </h2>
            </div>

            <div className="d-flex align-items-center gap-2">
              <Button
                size="sm"
                className="btn-neutral text-dark font-weight-500 border shadow-none btn-ip-action"
                onClick={() => navigate("/admin/failedLogin")}
              >
                {t("common.back", "Back")}
              </Button>

              {canUpdate && ipDetail && (
                ipDetail.isActive ? (
                  <Button
                    size="sm"
                    color="success"
                    className="font-weight-500 shadow-none border-0 btn-ip-unblock"
                    onClick={() => setStatusModalOpen(true)}
                  >
                    {t("page.blockedIps.actions.unblock", "Unblock IP")}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    color="danger"
                    className="font-weight-500 shadow-none border-0 btn-ip-block-orange"
                    onClick={() => setBlockModalOpen(true)}
                  >
                    {t("page.blockedIps.actions.block", "Block IP")}
                  </Button>
                )
              )}
            </div>
          </div>
          <CardBody className="p-4">
            {loadingDetail ? (
              <div className="center-loader py-4">
                <div className="center-loader-content">
                  <Spinner />
                  <p className="mt-3 text-muted">{t("common.loading", "Loading....")}</p>
                </div>
              </div>
            ) : !ipDetail ? (
              <div className="text-center py-4 text-muted">
                {t("page.blockedIps.noData", "No details found for this IP.")}
              </div>
            ) : (
              <Row className="gx-0">
                {/* Column 1: IP Address & Status */}
                <Col xs="12" sm="6" md="3" className="pr-md-4 border-right-md mb-3 mb-md-0">
                  <div className="mb-3">
                    <small className="text-muted text-xs d-block mb-1 font-weight-500">
                      {t("table.headers.ipAddress", "IP Address")}
                    </small>
                    <div className="font-weight-700 text-dark mb-1 font-mono ip-address-display">
                      {ipAddress}
                    </div>
                  </div>

                  <div>
                    <small className="text-muted text-xs d-block mb-1 font-weight-500">
                      {t("table.headers.status", "Status")}
                    </small>
                    <div className="d-flex align-items-center text-sm font-weight-600 text-dark">
                      {ipDetail.isActive ? (
                        <>
                          <i className="fa fa-times-circle text-danger mr-2 ip-icon-badge" />
                          <span>{t("page.blockedIps.status.blocked", "Blocked")}</span>
                        </>
                      ) : (
                        <>
                          <i className="fa fa-check-circle text-success mr-2 ip-icon-badge" />
                          <span>{t("page.blockedIps.status.notBlocked", "Not Blocked")}</span>
                        </>
                      )}
                    </div>
                  </div>
                </Col>

                {/* Column 2: Block Type, Source & Reason */}
                <Col xs="12" sm="6" md="3" className="px-md-4 border-right-md mb-3 mb-md-0">
                  <div className="mb-3">
                    <small className="text-muted text-xs d-block mb-1 font-weight-500">
                      {t("table.headers.blockType", "Block Type")}
                    </small>
                    {ipDetail.blockType === "PERMANENT" ? (
                      <Badge className="badge-pill px-2.5 py-0.5 text-uppercase badge-status-permanent">
                        {t("page.blockedIps.status.permanent", "PERMANENT")}
                      </Badge>
                    ) : ipDetail.blockType === "TEMPORARY" ? (
                      <Badge className="badge-pill px-2.5 py-0.5 text-uppercase badge-status-temporary">
                        {t("page.blockedIps.status.temporary", "TEMPORARY")}
                      </Badge>
                    ) : (
                      <span className="text-muted text-sm font-weight-500">N/A</span>
                    )}
                  </div>

                  <div className="mb-3">
                    <small className="text-muted text-xs d-block mb-1 font-weight-500">
                      {t("table.headers.source", "Source")}
                    </small>
                    <Badge
                      className={`badge-pill px-2.5 py-0.5 text-uppercase ${ipDetail.source === "ADMIN" ? "badge-source-admin" : "badge-source-system"}`}
                    >
                      {ipDetail.source === "ADMIN"
                        ? t("page.blockedIps.sources.admin", "Admin")
                        : t("page.blockedIps.sources.system", "System")}
                    </Badge>
                  </div>

                  <div>
                    <small className="text-muted text-xs d-block mb-1 font-weight-500">
                      {t("table.headers.reason", "Reason")}
                    </small>
                    <div className="text-sm font-weight-500 text-dark">
                      {formatReason(ipDetail.reason, t)}
                    </div>
                  </div>
                </Col>

                {/* Column 3: Blocked By, Blocked At, Blocked Until */}
                <Col xs="12" sm="6" md="3" className="px-md-4 border-right-md mb-3 mb-md-0">
                  <div className="mb-3">
                    <small className="text-muted text-xs d-block mb-1 font-weight-500">
                      {t("table.headers.blockedBy", "Blocked By")}
                    </small>
                    <div className="text-sm font-weight-500 text-dark">
                      {ipDetail.blockedBy?.firstName
                        ? `${ipDetail.blockedBy.firstName} ${ipDetail.blockedBy.lastName || ""}`
                        : (ipDetail.blockedBy?.email || "Super Admin")}
                    </div>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted text-xs d-block mb-1 font-weight-500">
                      {t("table.headers.blockedAt", "Blocked At")}
                    </small>
                    <div className="text-sm font-weight-500 text-dark">
                      {formatFullDateTime(ipDetail.blockedAt)}
                    </div>
                  </div>

                  <div>
                    <small className="text-muted text-xs d-block mb-1 font-weight-500">
                      {t("table.headers.blockedUntil", "Blocked Until")}
                    </small>
                    <div className="text-sm font-weight-500 text-dark mb-1">
                      {formatFullDateTime(ipDetail.blockedUntil)}
                    </div>
                    {durationInfo?.remainingText && !durationInfo.isExpired && (
                      <Badge className="badge-pill px-2 py-0.5 badge-status-temporary">
                        {durationInfo.remainingText}
                      </Badge>
                    )}
                  </div>
                </Col>

                {/* Column 4: Total Attempts, Last Login Attempt */}
                <Col xs="12" sm="6" md="3" className="pl-md-4">
                  <div className="mb-3">
                    <small className="text-muted text-xs d-block mb-1 font-weight-500">
                      {t("table.headers.totalAttempts", "Total Attempts")}
                    </small>
                    <div className="font-weight-700 text-danger ip-attempts-display">
                      {ipDetail.totalAttempts ?? 0}
                    </div>
                  </div>

                  <div>
                    <small className="text-muted text-xs d-block mb-1 font-weight-500">
                      {t("table.headers.lastLogin", "Last Login Attempt")}
                    </small>
                    <div className="text-sm font-weight-500 text-dark">
                      {formatFullDateTime(ipDetail.latestAttempt?.createdAt)}
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </CardBody>
        </Card>

        {/* ── CARD 2: ACCOUNT INFORMATION (3 HORIZONTAL ITEMS) ───────────── */}
        <Card className="shadow-sm border-0 mb-4 ip-security-card">
          <div className="py-3 px-4 border-bottom">
            <h5 className="mb-0 font-weight-bold text-dark ip-subheading-title">
              {t("page.blockedIps.section.accountInfo", "Account Information")}
            </h5>
          </div>
          <CardBody className="py-3 px-4">
            <Row className="align-items-center">
              {/* Item 1: Matched Account Name */}
              <Col md="4" className="border-right-md mb-3 mb-md-0 py-2">
                <div className="d-flex align-items-center">
                  <div className="ip-account-icon-box">
                    <i className="fa fa-user" />
                  </div>
                  <div>
                    <div className="text-muted text-xs font-weight-500 mb-0.5">
                      {t("table.headers.matchedUser", "Matched Account Name")}
                    </div>
                    <div className="text-sm font-weight-bold text-dark">
                      {ipDetail?.matched_user?.name || (
                        <Badge className="badge-pill px-2 py-0.5 text-uppercase font-weight-500 badge-no-account">
                          {t("page.blockedIps.noMatchingAccount", "No account")}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </Col>

              {/* Item 2: Email Address */}
              <Col md="4" className="border-right-md mb-3 mb-md-0 py-2 px-md-4">
                <div className="d-flex align-items-center">
                  <div className="ip-account-icon-box">
                    <i className="fa fa-envelope" />
                  </div>
                  <div>
                    <div className="text-muted text-xs font-weight-500 mb-0.5">
                      {t("table.headers.emailAddress", "Email Address")}
                    </div>
                    <div className="text-sm font-weight-bold text-dark font-mono">
                      {ipDetail?.attempted_email || ipDetail?.matched_user?.email || "N/A"}
                    </div>
                  </div>
                </div>
              </Col>

              {/* Item 3: Distinct Targeted Accounts */}
              <Col md="4" className="py-2 pl-md-4">
                <div className="d-flex align-items-center">
                  <div className="ip-account-icon-box">
                    <i className="fa fa-bullseye" />
                  </div>
                  <div>
                    <div className="text-muted text-xs font-weight-500 mb-0.5">
                      {t("table.headers.affectedAccounts", "Distinct Targeted Accounts")}
                    </div>
                    <div className="text-sm font-weight-bold text-dark">
                      {ipDetail?.affectedAccountsCount ?? (ipDetail?.matched_user ? 1 : 0)}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* ── CARD 3: SECURITY EVENT HISTORY TABLE ───────────────────────── */}
        <Card className="shadow-sm border-0 mb-4 ip-security-card">
          <div className="py-3 px-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
            <div>
              <h5 className="mb-0 font-weight-bold text-dark ip-subheading-title">
                {t("page.blockedIps.modals.historyTitle", "Security Event History")}
              </h5>
              <small className="text-muted text-xs">
                {t("page.blockedIps.historySubtitle", "Audit logs of all risk evaluations, triggers, and administrative actions for this IP.")}
              </small>
            </div>
            <Button
              size="sm"
              className="btn-neutral text-dark font-weight-500 border shadow-none btn-ip-refresh"
              onClick={fetchHistory}
              disabled={loadingHistory}
            >
              {t("common.refresh", "Refresh")}
            </Button>
          </div>

          <div>
            {loadingHistory && events.length === 0 ? (
              <div className="center-loader py-4">
                <div className="center-loader-content">
                  <Spinner />
                  <p className="mt-3 text-muted">{t("common.loading", "Loading....")}</p>
                </div>
              </div>
            ) : (
              <ToolkitProvider
                key={`history-toolkit-${language}`}
                data={events}
                keyField="_id"
                columns={columns}
                search={false}
              >
                {(props) => (
                  <div>
                    <div className="table-responsive">
                      <BootstrapTable
                        key={`history-table-${language}`}
                        {...props.baseProps}
                        columns={columns}
                        bootstrap4
                        bordered={false}
                        classes="table align-items-center table-flush"
                        headerClasses="thead-light"
                        remote={{ sort: true, pagination: true }}
                        onTableChange={handleTableChange}
                        noDataIndication={() => (
                          <div className="text-center py-5 text-muted">
                            {t("page.blockedIps.noEvents", "No security events recorded for this IP.")}
                          </div>
                        )}
                      />
                    </div>
                    <div className="pb-2">
                      <Pagination
                        totalRows={meta.totalItems || 0}
                        rowsPerPage={tableState.limit}
                        rowsPerPageOptions={LOGS_PAGE_SIZE_OPTIONS}
                        currentPage={tableState.page - 1}
                        onPageChange={(p) => setTableState((prev) => ({ ...prev, page: p + 1 }))}
                        onRowsPerPageChange={(size) =>
                          setTableState((prev) => ({ ...prev, limit: size, page: 1 }))
                        }
                        t={t}
                      />
                    </div>
                  </div>
                )}
              </ToolkitProvider>
            )}
          </div>
        </Card>
      </Container>

      {/* Unblock modal */}
      {ipDetail && (
        <IpStatusReasonModal
          isOpen={statusModalOpen}
          toggle={() => setStatusModalOpen(false)}
          selectedRow={ipDetail}
          onConfirm={handleStatusConfirm}
        />
      )}

      {/* Manual block modal */}
      {ipDetail && (
        <ManualBlockModal
          isOpen={blockModalOpen}
          toggle={() => setBlockModalOpen(false)}
          selectedRow={ipDetail}
          onConfirm={handleBlockConfirm}
        />
      )}
    </div>
  );
};

export default IpSecurityHistoryPage;
